import * as phoneLib from 'google-libphonenumber';
import * as yup from 'yup';

const phoneUtil = phoneLib.PhoneNumberUtil.getInstance();

// Custom Yup validators
yup.addMethod(yup.string, 'requiredWithSupplementary', function requiredWithSupplementaryMethod(message) {
  return this.test('requiredWithSupplementary', message, function requiredWithSupplementaryValidator(value) {
    const { parent } = this;
    if (!value && parent.category === 'supplementary') {
      return this.createError({ message: message || 'is Required with supplementary materials' });
    }
    return true;
  });
});

yup.addMethod(yup.string, 'isPhone', function isPhoneMethod(message) {
  return this.test('isPhone', message, function isPhoneValidator(value) {
    if (!value) return true;

    let phoneValue;
    try {
      phoneValue = phoneUtil.parse(value);
    } catch (error) {
      return this.createError({ message: message || 'Please enter a valid phone number' });
    }

    if (phoneUtil.isValidNumber(phoneValue) && !/[a-z]|[A-Z]/.test(value)) {
      return true;
    }

    return this.createError({ message: message || 'Please enter a valid phone number' });
  });
});

yup.addMethod(yup.date, 'isReportDate', function isReportDateMethod(message) {
  return this.test('isReportDate', message, function isReportDateValidator(value) {
    if (!value) return true;

    const dateValue = new Date(value);
    const dateDiff = dateValue.getTime() - new Date().getTime();
    const diffInHours = dateDiff / 1000 / 60 / 60;

    if (diffInHours < 72) {
      return this.createError({ message: message || 'Preferred Report Date can not be less than 5 days' });
    }
    if (diffInHours > 365 * 24) {
      return this.createError({ message: message || 'Preferred Report Date can not be more than one year' });
    }
    return true;
  });
});

yup.addMethod(yup.string, 'isPassword', function isPasswordMethod(message) {
  return this.test('isPassword', message, function isPasswordValidator(value) {
    if (!value) return true;

    const upperCaseRegex = /[A-Z]/;
    const numberRegex = /\d/;

    if (!upperCaseRegex.test(value)) {
      return this.createError({ message: message || 'must contain at least one upper case letter' });
    }
    if (!numberRegex.test(value)) {
      return this.createError({ message: message || 'must contain at least one number' });
    }

    return true;
  });
});

yup.addMethod(yup.date, 'isDOB', function isDOBMethod(message) {
  return this.test('isDOB', message, function isDOBValidator(value) {
    if (!value) return true;

    if (new Date(value) > new Date()) {
      return this.createError({ message: message || 'can not be in the future' });
    }
    return true;
  });
});

yup.addMethod(yup.date, 'isFutureDate', function isFutureDateMethod(message) {
  return this.test('isFutureDate', message, function isFutureDateValidator(value) {
    if (!value) return true;

    if (new Date(value) < new Date()) {
      return this.createError({ message: message || 'should be in the future' });
    }
    return true;
  });
});

// Custom email validator that trims whitespace
yup.addMethod(yup.string, 'trimmedEmail', function trimmedEmailMethod(message) {
  return this.test('trimmedEmail', message, function trimmedEmailValidator(value) {
    if (!value) return true;

    const trimmedValue = value.trim();
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!emailRegex.test(trimmedValue)) {
      return this.createError({ message: message || 'must be a valid email' });
    }
    return true;
  });
});

export default class Base {
  static async validate(item, fields, schema) {
    try {
      // Create a schema with only the specified fields
      const filteredSchema = yup.object().shape(
        Object.keys(schema.fields)
          .filter((key) => fields.indexOf(key) !== -1)
          .reduce((acc, key) => {
            acc[key] = schema.fields[key];
            return acc;
          }, {})
      );

      await filteredSchema.validate(item, { abortEarly: false });
      return null; // No errors
    } catch (error) {
      if (error.inner && error.inner.length > 0) {
        // Convert Yup errors to validate.js format
        const errors = {};

        error.inner.forEach((err) => {
          const fieldPath = err.path || 'unknown';
          if (!errors[fieldPath]) {
            errors[fieldPath] = [];
          }
          errors[fieldPath].push(err.message);
        });
        return errors;
      }

      // Single error case
      const fieldPath = error.path || 'unknown';
      const errorObj = { [fieldPath]: [error.message || 'Validation failed'] };

      return errorObj;
    }
  }

  clone(source) {
    if (source) {
      Object.keys(source).forEach((key) => {
        this[key] = source[key];
      });
    }
  }
}

export { yup };

// Add debug logging for yup validation
// eslint-disable-next-line no-console
console.log('🔍 Base.js loaded with yup');
